// Turns what someone typed in the note editor (a contenteditable) into the
// Markdown subset notes are stored as: paragraphs separated by a blank
// line, **bold**, _italic_, "- " bullet lists and "1. " numbered lists.
// Anything the browser adds that has no place in that subset — spans,
// styles, nested wrappers — is flattened to its text. The server renders
// the result back to HTML with NoteMarkdown, so nothing here is trusted
// for display.

const BLOCK_TAGS = new Set(['P', 'DIV', 'UL', 'OL', 'LI', 'BLOCKQUOTE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'PRE'])

export function htmlToMarkdown(root) {
  const blocks = []
  collectBlocks(root, blocks, 0)
  return blocks
    .map((block) => block.replace(/[ \t]+$/gm, ''))
    .filter((block) => block.trim() !== '')
    .join('\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function collectBlocks(node, blocks, depth) {
  let run = ''
  const flush = () => {
    if (run.trim() !== '') blocks.push(escapeLineStarts(run.trim()))
    run = ''
  }

  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      run += escapeInline(child.textContent)
      return
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return

    const tag = child.tagName
    if (tag === 'UL' || tag === 'OL') {
      flush()
      blocks.push(listToMarkdown(child, depth))
      return
    }
    if (BLOCK_TAGS.has(tag)) {
      flush()
      // A paragraph that the browser has wrapped around a list (Chrome does
      // this when a list is started inside an existing paragraph) is really
      // a sequence of blocks.
      if (child.querySelector('ul, ol')) {
        collectBlocks(child, blocks, depth)
      } else {
        const text = inlineToMarkdown(child).trim()
        if (text !== '') blocks.push(escapeLineStarts(text))
      }
      return
    }
    run += inlineChildToMarkdown(child)
  })

  flush()
}

function listToMarkdown(list, depth) {
  const ordered = list.tagName === 'OL'
  const indent = '  '.repeat(depth)
  const lines = []
  let index = 0

  list.childNodes.forEach((item) => {
    if (item.nodeType !== Node.ELEMENT_NODE || item.tagName !== 'LI') return
    index += 1
    const marker = ordered ? `${index}. ` : '- '
    const nested = []
    let text = ''

    item.childNodes.forEach((part) => {
      if (part.nodeType === Node.ELEMENT_NODE && (part.tagName === 'UL' || part.tagName === 'OL')) {
        nested.push(listToMarkdown(part, depth + 1))
      } else {
        text += inlineChildToMarkdown(part)
      }
    })

    const body = text.trim().replace(/\n+/g, '\n' + indent + '  ')
    if (body !== '' || nested.length) lines.push(indent + marker + body)
    nested.forEach((block) => lines.push(block))
  })

  return lines.join('\n')
}

// The inline Markdown for everything inside `node`.
function inlineToMarkdown(node) {
  let out = ''
  node.childNodes.forEach((child) => { out += inlineChildToMarkdown(child) })
  return out
}

// The inline Markdown for one node, including its own emphasis — so a
// <b> that sits directly inside a list item keeps its bold.
function inlineChildToMarkdown(child) {
  if (child.nodeType === Node.TEXT_NODE) return escapeInline(child.textContent)
  if (child.nodeType !== Node.ELEMENT_NODE) return ''

  const tag = child.tagName
  if (tag === 'BR') return '\n'

  const inner = inlineToMarkdown(child)
  if (inner.trim() === '') return inner
  if (tag === 'B' || tag === 'STRONG' || isBoldStyle(child)) return wrap(inner, '**')
  if (tag === 'I' || tag === 'EM' || isItalicStyle(child)) return wrap(inner, '_')
  if (tag === 'A' && child.getAttribute('href')) return `[${inner}](${child.getAttribute('href')})`
  return inner
}

// Emphasis markers must hug the text: "** bold **" is not bold in Markdown.
function wrap(text, marker) {
  const leading = text.match(/^\s*/)[0]
  const trailing = text.match(/\s*$/)[0]
  const core = text.trim()
  return core === '' ? text : `${leading}${marker}${core}${marker}${trailing}`
}

function isBoldStyle(el) {
  const weight = el.style && el.style.fontWeight
  return weight === 'bold' || parseInt(weight, 10) >= 600
}

function isItalicStyle(el) {
  return !!(el.style && el.style.fontStyle === 'italic')
}

// Characters that would otherwise be read as Markdown inside a run of text.
function escapeInline(text) {
  return text.replace(/ /g, ' ').replace(/([*_`\\])/g, '\\$1')
}

// A line that happens to start like a heading, quote, rule or list marker
// stays literal. Real lists come through listToMarkdown, not here.
function escapeLineStarts(text) {
  return text
    .split('\n')
    .map((line) => line
      .replace(/^(\s*)([#>]|[-+]\s|---)/, '$1\\$2')
      .replace(/^(\s*\d+)(\.\s)/, '$1\\$2'))
    .join('\n')
}
